import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useRef } from "react";
import { underlineNavigationTabClassName } from "@/components/ui/tabs";

export type LessonSubject = "basics" | "openings" | "tactics" | "strategy" | "endgames";

export default function LessonLibraryNavigation({
  subject,
}: {
  subject: LessonSubject;
}) {
  const { t } = useTranslation("common");
  const navigation = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = navigation.current;
    if (!element) return;
    const showSubject = () => {
      const current = element.querySelector('[aria-current="page"]');
      if (!current) return;
      const viewport = element.getBoundingClientRect();
      const tab = current.getBoundingClientRect();
      if (tab.right > viewport.right)
        element.scrollLeft += tab.right - viewport.right;
      else if (tab.left < viewport.left)
        element.scrollLeft -= viewport.left - tab.left;
    };
    showSubject();
    const observer = new ResizeObserver(showSubject);
    observer.observe(element);
    return () => observer.disconnect();
  }, [subject]);
  return (
    <nav
      ref={navigation}
      aria-label={t("lessonLibrary.subjects")}
      className="mb-6 overflow-x-auto border-b border-athens-gray-200"
    >
      <div className="flex w-max gap-4 md:gap-6">
        {(["basics", "openings", "tactics", "strategy", "endgames"] as const).map(
          (item) => (
            <Link
              key={item}
              href={
                item === "openings"
                  ? "/learn/openings"
                  : `/learn/lessons#${item}`
              }
              scroll={false}
              aria-current={subject === item ? "page" : undefined}
              data-state={subject === item ? "active" : "inactive"}
              className={underlineNavigationTabClassName}
            >
              {t(
                item === "openings" ? "Openings" : `foundationLessons.${item}`,
              )}
            </Link>
          ),
        )}
      </div>
    </nav>
  );
}
